import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

import { filterViewModel } from '@lib/filterViewModel';
import { initializeMap } from '@lib/MapHelper';
import mcmMarkerImg from '@static/icons/mcm-marker.svg';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_IMAGE_BASE_URL, MAX_MAP_SIZE } from '@lib/const';

import type { Layer } from '@graphql';
import type React from 'react';
import type { LatLngCoordinates } from '@lib/types';
import type { ApolloClient } from '@lib/apolloClient';

type MapProps = {
  isVisible: boolean;
};

type WrapperProps = {
  backgroundUrl?: string;
};

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  height: 100%;
  position: relative;
  background-repeat: no-repeat;
  background-size: cover;
  ${({ backgroundUrl }) =>
    backgroundUrl &&
    `
      background-image: url(${backgroundUrl});
    `}
`;

const StyledMap = styled.div<MapProps>`
  width: 100%;
  height: 100%;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  #marker {
    top: -15px;
    display: none;
    img {
      width: 48px;
      height: 53px;
    }
  }
`;

type Props = {
  layerType?: Layer;
  coordinates: LatLngCoordinates;
};

export const Map: React.FC<Props> = ({ layerType, coordinates }) => {
  const [initialized, setInitialized] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState<string>();
  const apolloClient = useApolloClient() as ApolloClient;

  const wrapperRefCallback = (node: HTMLDivElement): void => {
    if (node && !backgroundUrl) {
      const { clientWidth, clientHeight } = node;
      const width = Math.min(MAX_MAP_SIZE, clientWidth);
      const height = Math.min(MAX_MAP_SIZE, clientHeight);
      setBackgroundUrl(
        `${MAPBOX_IMAGE_BASE_URL}${filterViewModel.center?.join(',') || ''},${
          filterViewModel.zoom || ''
        },0/${width}x${height}?access_token=${MAPBOX_ACCESS_TOKEN}`,
      );
    }
  };

  useEffect(() => {
    const initCallback = (): void => {
      setInitialized(true);
      window.removeEventListener('scroll', initCallback);
      window.removeEventListener('touchstart', initCallback);
      window.removeEventListener('mousemove', initCallback);
    };
    window.addEventListener('scroll', initCallback);
    window.addEventListener('touchstart', initCallback);
    window.addEventListener('mousemove', initCallback);
    if (!window.Mapbox) {
      const zoom = filterViewModel.zoom;
      const center = filterViewModel.center || coordinates;

      initializeMap(center, layerType, zoom as number, apolloClient, () =>
        setTimeout(initCallback, 8000),
      );
    }

    return () => {
      window.removeEventListener('scroll', initCallback);
      window.removeEventListener('touchstart', initCallback);
      window.removeEventListener('mousemove', initCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper backgroundUrl={backgroundUrl} ref={wrapperRefCallback}>
      <StyledMap id="map" isVisible={initialized}>
        <div id="marker">
          <img src={mcmMarkerImg} alt="Marker" />
        </div>
      </StyledMap>
    </Wrapper>
  );
};
